import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Local cache keys. We keep two: one for guest mode (preserved so guest can
// sign up later without losing progress) and one for the offline cache of the
// signed-in user's server collection.
const GUEST_KEY = 'fortnite_sprite_collection_v2';
const AUTHED_KEY = 'sprite-vault:collection:server-cache:v1';
const HYDRATED_FLAG = 'sprite-vault:collection:server-hydrated:v1';

// How long we wait after the last local edit before pushing to the server.
const SAVE_DEBOUNCE_MS = 900;

const loadLocal = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};

const saveLocal = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota exceeded / private mode — ignore */
  }
};

export function useCollection() {
  const { isAuthed, user } = useAuth();

  // Initial state = latest of the two caches. Prefer authed cache when signed
  // in so the UI paints instantly with the last-known server state instead of
  // flickering to guest data first.
  const initial = isAuthed && user?.id ? loadLocal(AUTHED_KEY) : loadLocal(GUEST_KEY);
  const [collection, setCollection] = useState(initial);

  // Guard so we don't push server writes while we're still hydrating from server
  const hydratedRef = useRef(!isAuthed); // guest mode is "hydrated" immediately
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef(false);
  const currentUserIdRef = useRef(user?.id || null);

  // ── Hydrate from server on sign-in ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    // signed out → reload guest cache and mark ready
    if (!isAuthed || !user?.id) {
      currentUserIdRef.current = null;
      hydratedRef.current = true;
      setCollection(loadLocal(GUEST_KEY));
      return () => { cancelled = true; };
    }

    // signed-in path
    currentUserIdRef.current = user.id;
    hydratedRef.current = false;

    (async () => {
      try {
        const remote = await api.getMyCollection();
        if (cancelled) return;
        const serverEntries = remote?.entries || {};

        // If guest had local progress AND the server is empty, upload it once.
        // Track this so we don't merge on every mount.
        const localGuest = loadLocal(GUEST_KEY);
        const serverIsEmpty = !serverEntries || Object.keys(serverEntries).length === 0;
        const localHasData = Object.keys(localGuest).length > 0;
        const alreadyMerged = localStorage.getItem(HYDRATED_FLAG) === user.id;

        if (localHasData && serverIsEmpty && !alreadyMerged) {
          try {
            const merged = await api.mergeMyCollection(localGuest);
            if (!cancelled) {
              const next = merged?.entries || localGuest;
              setCollection(next);
              saveLocal(AUTHED_KEY, next);
            }
          } catch {
            // If merge fails, at least render local data locally.
            if (!cancelled) setCollection(localGuest);
          }
          try { localStorage.setItem(HYDRATED_FLAG, user.id); } catch { /* ignore */ }
        } else {
          setCollection(serverEntries);
          saveLocal(AUTHED_KEY, serverEntries);
          try { localStorage.setItem(HYDRATED_FLAG, user.id); } catch { /* ignore */ }
        }
      } catch {
        // Server unreachable — keep offline cache in state so the app stays usable.
        if (!cancelled) setCollection(loadLocal(AUTHED_KEY));
      } finally {
        if (!cancelled) hydratedRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthed, user?.id]);

  // ── Persist changes: localStorage every time, server debounced when authed ──
  useEffect(() => {
    // Always keep an offline cache so the UI stays snappy across reloads.
    const cacheKey = isAuthed && user?.id ? AUTHED_KEY : GUEST_KEY;
    saveLocal(cacheKey, collection);

    if (!isAuthed || !hydratedRef.current) return;

    // Debounce the network write so rapid clicks (five level-dot taps in a
    // row) coalesce into a single PUT.
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    pendingSaveRef.current = true;
    saveTimerRef.current = setTimeout(async () => {
      pendingSaveRef.current = false;
      try {
        await api.putMyCollection(collection);
      } catch {
        // best-effort; local cache is still authoritative until the next save
      }
    }, SAVE_DEBOUNCE_MS);

    return () => {
      // no cleanup here — we want the timer to fire even between renders
    };
  }, [collection, isAuthed, user?.id]);

  // Flush any pending debounced save before unload so navigating away doesn't
  // drop the last click.
  useEffect(() => {
    const onBeforeUnload = () => {
      if (pendingSaveRef.current && isAuthed) {
        try {
          navigator.sendBeacon?.(
            (typeof window !== 'undefined' ? (window.__API_URL__ || '') : '') || '/api/collections/me',
            new Blob(
              [JSON.stringify({ entries: collection })],
              { type: 'application/json' },
            ),
          );
        } catch { /* ignore */ }
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [collection, isAuthed]);

  // ── Mutators (identical public API to the old localStorage-only hook) ──────
  const getEntry = useCallback(
    (id) => collection[id] || { owned: false, level: 0, mastered: false },
    [collection]
  );

  const toggleOwned = useCallback((id) => {
    setCollection((prev) => {
      const cur = prev[id] || { owned: false, level: 0, mastered: false };
      const nextOwned = !cur.owned;
      return {
        ...prev,
        [id]: {
          owned: nextOwned,
          level: nextOwned ? Math.max(1, cur.level) : 0,
          // Mastered is a persistent achievement — un-owning clears it, but
          // if the user re-owns later the flag has to be re-earned.
          mastered: nextOwned ? cur.mastered : false,
        },
      };
    });
  }, []);

  const setLevel = useCallback((id, level) => {
    setCollection((prev) => {
      const cur = prev[id] || { owned: false, level: 0, mastered: false };
      const clamped = Math.max(0, Math.min(5, level));
      // Mastered is sticky: reaching 5 grants it, and dropping back down
      // (e.g. duplicates lost in-game) does NOT revoke it. Only a full
      // un-own clears the achievement.
      const nextMastered = clamped === 5 ? true : cur.mastered;
      return {
        ...prev,
        [id]: {
          owned: clamped > 0 || nextMastered ? true : cur.owned,
          level: clamped,
          mastered: nextMastered,
        },
      };
    });
  }, []);

  const toggleMastered = useCallback((id) => {
    setCollection((prev) => {
      const cur = prev[id] || { owned: false, level: 0, mastered: false };
      const nextMastered = !cur.mastered;
      return {
        ...prev,
        [id]: {
          // Turning mastered on marks the sprite as owned but does NOT
          // force the level to 5 — the user keeps whatever level they had
          // (Fortnite lets you be Mastered while sitting at Lv 1 again
          // after losing duplicates).
          owned: nextMastered ? true : cur.owned,
          level: nextMastered ? Math.max(cur.level, 1) : cur.level,
          mastered: nextMastered,
        },
      };
    });
  }, []);

  const reset = useCallback(async () => {
    setCollection({});
    if (isAuthed) {
      try { await api.resetMyCollection(); } catch { /* ignore */ }
    }
  }, [isAuthed]);

  return { collection, getEntry, toggleOwned, setLevel, toggleMastered, reset };
}
